import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, WHITE, ROLES } from '../lib/ui';
import { Avatar, Btn, Input, Select, Field, Card } from '../lib/ui';


export default function TeamView({ team, onAdd, onDelete, allTasks, currentUserEmail, isAdmin }) {
  const [form,    setForm]    = useState({ name: '', email: '', password: '', role: 'Lawyer' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const valid   = form.name.trim() && form.email.trim() && form.password.length >= 6;

  const handleAdd = async () => {
    if (!valid || !isAdmin) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const { error: authErr } = await supabase.auth.signUp({
        email: form.email, password: form.password,
        options: { data: { full_name: form.name } }
      });
      if (authErr) throw authErr;
      await onAdd({ name: form.name, role: form.role, email: form.email });
      setSuccess(`Account created for ${form.name} (${form.email}).`);
      setForm({ name: '', email: '', password: '', role: 'Lawyer' });
    } catch (e) {
      setError(e.message || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Admin badge */}
      {isAdmin && (
        <div style={{ background: NAVY, borderRadius: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔑</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: GOLD }}>Admin Access</div>
            <div style={{ fontSize: 11, color: '#6A7A9A' }}>You can add and remove team members</div>
          </div>
        </div>
      )}

      {/* Team cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
        {team.map(m => {
          const assigned  = allTasks.filter(t => (t.assignees || []).includes(m.id));
          const active    = assigned.filter(t => t.status === 'In Progress').length;
          const completed = assigned.filter(t => t.status === 'Completed').length;
          const isYou     = m.email === currentUserEmail;
          return (
            <div key={m.id} style={{ background: WHITE, borderRadius: 10, border: `1px solid ${isYou ? GOLD : '#E5E0D8'}`, padding: '18px 20px', position: 'relative', borderTop: isYou ? `3px solid ${GOLD}` : '1px solid #E5E0D8' }}>
              {isYou && <div style={{ position: 'absolute', top: 10, left: 12, fontSize: 10, color: GOLD, fontWeight: 700, letterSpacing: 1 }}>YOU</div>}
              {/* Only admin sees the remove button, and can't remove themselves */}
              {isAdmin && !isYou && (
                <button onClick={() => { if(window.confirm(`Remove ${m.name} from the team?`)) onDelete(m.id); }}
                  style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: '#DDD', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              )}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, marginTop: isYou ? 8 : 0 }}>
                <Avatar name={m.name} size={44} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, fontFamily: 'Georgia,serif' }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: GOLD, fontWeight: 600 }}>{m.role}</div>
                  {m.email && <div style={{ fontSize: 11, color: '#AAA', marginTop: 2 }}>{m.email}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#777' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 700, color: NAVY, fontSize: 20 }}>{assigned.length}</div><div>Total</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 700, color: '#2980B9', fontSize: 20 }}>{active}</div><div>Active</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 700, color: '#27AE60', fontSize: 20 }}>{completed}</div><div>Done</div></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create account — admin only */}
      {isAdmin ? (
        <Card title="Create New Team Member Account">
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.6 }}>
            Creates a login account and adds the person to the team.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Full Name"><Input placeholder="e.g. Sara Yildiz" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Role"><Select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>{ROLES.map(r => <option key={r}>{r}</option>)}</Select></Field>
            <Field label="Email Address"><Input type="email" placeholder="sara@koluman.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Temporary Password"><Input type="password" placeholder="Min. 6 characters" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></Field>
          </div>
          {error   && <div style={{ marginTop:12, background:'#FDF0EE', border:'1px solid #F5C0B8', borderRadius:6, padding:'10px 14px', fontSize:13, color:'#C0392B' }}>{error}</div>}
          {success && <div style={{ marginTop:12, background:'#EAF7EE', border:'1px solid #A8DDB8', borderRadius:6, padding:'10px 14px', fontSize:13, color:'#27AE60' }}>{success}</div>}
          <div style={{ marginTop: 16 }}>
            <Btn onClick={handleAdd} style={{ opacity: valid && !loading ? 1 : 0.5 }}>
              {loading ? 'Creating...' : 'Create Account'}
            </Btn>
          </div>
          <div style={{ marginTop: 14, fontSize: 12, color: '#AAA', lineHeight: 1.7 }}>
            To change a password or fully delete an account: <strong>Supabase → Authentication → Users</strong>
          </div>
        </Card>
      ) : (
        /* Non-admin sees a read-only notice */
        <div style={{ background: '#F7F5F0', border: '1px solid #E5E0D8', borderRadius: 8, padding: '16px 18px', fontSize: 13, color: '#888', textAlign: 'center' }}>
          Contact <strong style={{ color: NAVY }}>Emre Koluman</strong> to add or remove team members.
        </div>
      )}
    </div>
  );
}
